import { authConfig } from '../config/auth';

export interface TokenResponse {
  id_token: string;
  access_token: string;
  expires_in: number;
  token_type: string;
  refresh_token?: string;
}

class TokenRefreshService {
  private refreshTimer: number | null = null;

  async exchangeCodeForTokens(code: string): Promise<TokenResponse> {
    const tokenEndpoint = `${authConfig.domain}/oauth2/token`;

    const params = new URLSearchParams({
      grant_type: 'authorization_code',
      client_id: authConfig.userPoolWebClientId,
      code: code,
      redirect_uri: authConfig.redirectSignIn,
    });

    const response = await fetch(tokenEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: params.toString(),
    });

    if (!response.ok) {
      const error = await response.text();
      throw new Error(`Token exchange failed: ${error}`);
    }

    return response.json();
  }

  async refreshTokens(refreshToken: string): Promise<TokenResponse> {
    const tokenEndpoint = `${authConfig.domain}/oauth2/token`;

    const params = new URLSearchParams({
      grant_type: 'refresh_token',
      client_id: authConfig.userPoolWebClientId,
      refresh_token: refreshToken,
    });

    const response = await fetch(tokenEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: params.toString(),
    });

    if (!response.ok) {
      const error = await response.text();
      throw new Error(`Token refresh failed: ${error}`);
    }

    return response.json();
  }

  scheduleTokenRefresh(expiresIn: number, refreshToken: string, onRefresh: (tokens: TokenResponse) => void) {
    // Clear any existing timer
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
    }

    // Refresh 5 minutes before expiry (or halfway through if token expires in less than 10 minutes)
    const refreshTime = Math.max(expiresIn * 1000 - 5 * 60 * 1000, expiresIn * 500);

    this.refreshTimer = setTimeout(async () => {
      try {
        const newTokens = await this.refreshTokens(refreshToken);
        onRefresh(newTokens);

        // Schedule next refresh
        this.scheduleTokenRefresh(
          newTokens.expires_in,
          newTokens.refresh_token || refreshToken,
          onRefresh
        );
      } catch (error) {
        console.error('Failed to refresh tokens:', error);
        // Token refresh failed - user needs to re-authenticate
        window.dispatchEvent(new CustomEvent('auth:session-expired'));
      }
    }, refreshTime);
  }

  cancelTokenRefresh() {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
  }
}

export const tokenRefreshService = new TokenRefreshService();
